// src/components/LoginModal.js
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../api/api";
import { AuthContext } from "../context/AuthContext";
import "./LoginModal.css";

const LoginModal = ({ isOpen, onClose }) => {
  const { login } = useContext(AuthContext);
  const [identifier, setIdentifier] = useState(""); // Changed from email to identifier
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setIdentifier("");
      setPassword("");
      setError("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Try login with identifier (can be email OR username)
      const userData = await loginUser({ email: identifier, password });
      
      // DEBUG: Log the raw API response
      console.log("=== LOGIN API RESPONSE ===");
      console.log("Raw userData:", userData);
      console.log("Type:", typeof userData);
      console.log("Has 'user' property?", "user" in userData);
      console.log("userData.username:", userData?.username);
      console.log("userData.user?.username:", userData?.user?.username);
      console.log("=========================");
      
      // Pass to AuthContext
      login(userData);
      onClose();
      
      // Redirect based on role
      const userRole = userData?.user?.role || userData?.role || "USER";
      if (userRole === "ADMIN") {
        navigate("/admin-dashboard");
      } else if (userRole === "WRITER") {
        navigate("/writer-dashboard");
      } else {
        navigate("/student-dashboard");
      }
    } catch (err) {
      // Handle different error types with specific messages
      console.error("Login error details:", err);
      
      if (err.message && err.message.includes("Invalid")) {
        setError("Invalid email/username or password. Please try again.");
      } else if (err.message && err.message.includes("Account")) {
        setError("Account not found. Please check your credentials or register.");
      } else if (err.message && err.message.includes("Network")) {
        setError("Network error. Please check your connection.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    onClose();
    navigate("/forgot-password");
  };

  const handleRegister = () => {
    onClose();
    navigate("/register");
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay" 
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Login modal"
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Log In / Sign Up</h2>
          <p className="modal-subtitle">For Clients Only</p>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            aria-label="Close login modal"
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="modal-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="login-identifier">Email or Username</label> {/* Changed label */}
              <input
                id="login-identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your email or username"
                required
                disabled={isLoading}
                autoComplete="username"
                aria-describedby={error ? "error-message" : undefined}
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
                autoComplete="current-password"
                aria-describedby={error ? "error-message" : undefined}
              />
            </div>

            <button 
              type="submit" 
              className="modal-submit-btn"
              disabled={isLoading || !identifier || !password}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Logging in...
                </>
              ) : (
                "Log In"
              )}
            </button>
          </form>

          <div className="modal-footer">
            <button
              type="button"
              className="modal-link-btn"
              onClick={handleForgotPassword}
              disabled={isLoading}
            >
              Forgot Password?
            </button>

            <div className="modal-divider">
              <span>OR</span>
            </div>

            <div className="modal-register-section">
              <p className="modal-register-text">
                Don't have an account?
              </p>
              <button
                type="button"
                className="modal-register-btn"
                onClick={handleRegister}
                disabled={isLoading}
              >
                Register Here
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;